/**
 * Client-side architect recap fallback when the server cannot parse model JSON.
 *
 * @module client/utils/architectGracefulDegrade
 */

const TENANT_CONNECTED_BUTTONS = [
  {
    id: "fix-critical-first",
    label: "Fix the critical issues first",
    description: "Start with highest-severity findings",
  },
  {
    id: "show-full-picture",
    label: "Show me the full picture",
    description: "Broader walkthrough of all findings",
  },
  {
    id: "specific-task",
    label: "I have something specific to do",
    description: "User has a concrete task in mind",
  },
];

const DISCOVERY_BUTTONS = [
  { id: "src-active-directory", label: "Active Directory or LDAP", description: "" },
  { id: "src-hr-system", label: "HR system (Workday, BambooHR…)", description: "" },
  { id: "src-google-azure", label: "Google Workspace or cloud IdP", description: "" },
  { id: "src-cloud-only", label: "Cloud only — no directory", description: "" },
  { id: "src-not-sure", label: "Not sure — help me decide", description: "" },
];

/**
 * @param {unknown} opts
 */
export function shouldGracefulDegradeArchitectError(err, opts = {}) {
  if (err?.code === "rate_limit") return false;
  if (opts?.gracefulDegradeOnFailure) return true;
  const mode = String(opts?.interactionMode || "").trim().toLowerCase();
  return ["tenant_connected", "greenfield", "discovery", "resume"].includes(mode);
}

/**
 * @param {string | null | undefined} interactionMode
 */
function optionButtonsForMode(interactionMode) {
  const mode = String(interactionMode || "chat").trim().toLowerCase();
  if (mode === "tenant_connected" || mode === "resume") return TENANT_CONNECTED_BUTTONS;
  if (mode === "greenfield" || mode === "discovery") return DISCOVERY_BUTTONS;
  return TENANT_CONNECTED_BUTTONS;
}

/**
 * @param {string | null | undefined} interactionMode
 */
function fallbackMessageForMode(interactionMode) {
  const mode = String(interactionMode || "chat").trim().toLowerCase();
  if (mode === "tenant_connected" || mode === "resume") {
    return "Assessment complete — review the findings above. I can help you prioritize what to tackle next.";
  }
  if (mode === "greenfield" || mode === "discovery") {
    return "Let's build your identity plan step by step — pick a starting point below or tell me more about your environment.";
  }
  return "I'm ready to help — choose an option below or describe what you want to work on.";
}

/**
 * @param {unknown} opts
 */
export function buildClientArchitectFallback(opts = {}) {
  const message = fallbackMessageForMode(opts?.interactionMode);
  return {
    message,
    text: message,
    optionButtons: optionButtonsForMode(opts?.interactionMode),
    architectureSteps: [],
  };
}
